import { ArrowRight, Brain, Code, ShoppingCart, Globe, Cpu } from "lucide-react";
import { useTranslation } from 'react-i18next';

const Hero = () => {
    const { t } = useTranslation();

    const servicePills = [
        t('hero.pills.ai'),
        t('hero.pills.software'),
        t('hero.pills.ecommerce'),
        t('hero.pills.web'),
        t('hero.pills.wordpress'),
    ];

    return (
        <header
            className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white relative overflow-hidden pt-24"
            role="banner"
        >
            {/* Animated grid background */}
            <div
                className="absolute inset-0 opacity-40"
                style={{
                    backgroundImage: 'linear-gradient(rgba(59,130,246,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.07) 1px, transparent 1px)',
                    backgroundSize: '64px 64px',
                }}
                aria-hidden="true"
            />
            {/* Radial glow */}
            <div
                className="absolute inset-0"
                style={{ background: 'radial-gradient(ellipse 80% 60% at 50% -10%, rgba(59,130,246,0.18), transparent)' }}
                aria-hidden="true"
            />

            {/* Floating icons */}
            <div className="absolute top-32 left-16 animate-pulse opacity-25" aria-hidden="true">
                <Brain className="w-10 h-10 text-cyan-400" />
            </div>
            <div className="absolute top-44 right-20 animate-pulse delay-700 opacity-25" aria-hidden="true">
                <Cpu className="w-8 h-8 text-purple-400" />
            </div>
            <div className="absolute bottom-52 left-28 animate-pulse delay-1000 opacity-25" aria-hidden="true">
                <Code className="w-8 h-8 text-blue-400" />
            </div>
            <div className="absolute bottom-44 right-16 animate-pulse delay-500 opacity-25" aria-hidden="true">
                <ShoppingCart className="w-8 h-8 text-cyan-300" />
            </div>
            <div className="absolute top-60 left-1/4 animate-pulse delay-300 opacity-20" aria-hidden="true">
                <Globe className="w-6 h-6 text-blue-300" />
            </div>

            <div className="container mx-auto px-6 py-16 relative z-10">
                <div className="max-w-4xl mx-auto text-center">
                    {/* Available badge */}
                    <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-4 py-2 mb-8">
                        <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" aria-hidden="true" />
                        <span className="text-blue-300 text-sm font-medium">{t('hero.badge')}</span>
                    </div>

                    {/* Headline */}
                    <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                        <span className="bg-gradient-to-r from-white via-blue-100 to-cyan-100 bg-clip-text text-transparent">
                            {t('hero.headline1')}
                        </span>
                        <br />
                        <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
                            {t('hero.headline2')}
                        </span>
                    </h1>

                    {/* Description */}
                    <p className="text-lg md:text-xl text-gray-400 mb-6 max-w-2xl mx-auto leading-relaxed">
                        {t('hero.description')}
                    </p>

                    {/* Service pills */}
                    <div className="flex flex-wrap justify-center gap-2 mb-10" aria-label="Services offered">
                        {servicePills.map((s) => (
                            <span
                                key={s}
                                className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-sm text-gray-300"
                            >
                                {s}
                            </span>
                        ))}
                    </div>

                    {/* CTAs */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <button
                            onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                            className="group bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-200 hover:shadow-xl hover:shadow-blue-500/25 flex items-center gap-2 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                            aria-label={t('hero.getStarted')}
                        >
                            {t('hero.getStarted')}
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                        </button>
                        <button
                            onClick={() => document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' })}
                            className="border border-white/20 hover:border-white/40 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-200 hover:bg-white/5 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                            aria-label={t('hero.viewPortfolio')}
                        >
                            {t('hero.viewPortfolio')}
                        </button>
                    </div>

                </div>
            </div>

        </header>
    );
};

export default Hero;
